import React, { useEffect, useRef, useState } from 'react';
import { Anchor, FieldController, GridItemLayout, GridLayout, NameValidator, NumbersValidator, RadioButtonCard, TextField, useMatchScreenWidth } from '@keplerco/core';
import { FieldValues, useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../../../overmind';
import { AssessmentSubType } from '../../../../enums/assessment-type';
import classNames from 'classnames';
import styles from './details-step.module.css';
import { DetailsStepSkeleton } from './details-step.skeleton';
import { IDetailsStepLayoutProps } from './details-step.models';
import { EntitiesCardWidget } from '../../../../components/cards/entities-card.widget';
import layoutStyles from '../../../../design/layout.module.css';
import { AssessmentRequest } from '../../../../overmind/actions';
import { AssessmentStatus } from '../../../../enums/assessment-status';

export function DetailsStepLayout({ assessmentType, assessmentSlug, setAssessmentSlug, isLoading, setIsLoading, details, setDetails, onNext }: IDetailsStepLayoutProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const fieldValuesRef = useRef<HTMLFormElement>(null);

  const [fieldValues, setFieldValues] = useState<FieldValues>();

  const isMobile = useMatchScreenWidth('mobile');

  const { control, watch, setValue, trigger } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  useEffect(() => {
    watch(setFieldValues);
  }, []);

  useEffect(() => {
    setValue('name', details?.name);
    setValue('keplerPointsAvailable', details?.keplerPointsAvailable);
    setValue('forceAssessment', details?.forceAssessment);
  }, [details]);

  if (isLoading) return <DetailsStepSkeleton />;

  return (
    <div className={styles.layout}>
      <EntitiesCardWidget title="Details" description=" Name your assessment, and choose how many Kepler Points you would like to award">
        <form ref={fieldValuesRef} id="assessmentDetails">
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: 15 }}>
            <FieldController
              control={control}
              name="name"
              rules={new NameValidator()}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Assessment name"
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Enter a name',
                  }}
                />
              )}
            />

            <FieldController
              control={control}
              name="keplerPointsAvailable"
              rules={new NumbersValidator()}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Kepler Points"
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Enter a number',
                  }}
                />
              )}
            />
          </div>
        </form>
      </EntitiesCardWidget>

      <EntitiesCardWidget title="Preferences" description="Choose the preferences for this assessment.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
          <h4>Do you want to force participants to complete this assessment once activated?</h4>

          {!isMobile && <span style={{ color: 'var(--accent-2)', marginBottom: 10 }}>Force participants to complete an assessment, the next time someone logs in they will be required to complete the assessment before continuing.</span>}

          <FieldController
            control={control}
            name="forceAssessment"
            render={({ field }) => (
              <GridLayout columnCount={isMobile ? 1 : 2}>
                <GridItemLayout>
                  <RadioButtonCard
                    id="forceAssessmentTrue"
                    checked={field.value}
                    onChange={() => field.onChange(true)}
                  >
                    <div className="card">Yes, make this compulsory</div>
                  </RadioButtonCard>
                </GridItemLayout>

                <GridItemLayout>
                  <RadioButtonCard
                    id="forceAssessmentFalse"
                    checked={!field.value}
                    onChange={() => field.onChange(false)}
                  >
                    <div className="card">No, make optional</div>
                  </RadioButtonCard>
                </GridItemLayout>
              </GridLayout>
            )}
          />

          <span className={classNames({ invisible: !fieldValues?.forceAssessment })} style={{ color: 'var(--accent-2)' }}>
            Great! We'll let people know to complete the assessment.
          </span>
        </div>
      </EntitiesCardWidget>

      <footer className={classNames('card', layoutStyles.wizardFooter)} style={{ justifyContent: 'flex-end' }}>
        <Anchor
          arrow
          onClick={async () => {
            const isValid = await trigger(['name', 'keplerPointsAvailable']);

            if (!fieldValues || !isValid) {
              if (!!fieldValuesRef.current) fieldValuesRef.current.scrollIntoView(false);
              return;
            }

            setIsLoading(true);

            const detailsRequest: AssessmentRequest = {
              companySlug: companyVariables.slug!,
              assessmentType,
              assessmentStatus: details?.assessmentStatus ?? AssessmentStatus.Draft,
              slug: assessmentSlug,
              name: fieldValues?.name,
              keplerPointsAvailable: parseInt(fieldValues?.keplerPointsAvailable),
              description: '',
              minScore: details?.minScore ?? 0,
              maxScore: details?.maxScore ?? 0,
              allowReassessment: !!details?.allowReassessment,
              forceAssessment: !!fieldValues?.forceAssessment,
              assessmentSubType: AssessmentSubType.GroupAssessment,
            };
            const response = await actions.updateAssessment(detailsRequest);

            if (!response) {
              setIsLoading(false);
              return;
            }

            setAssessmentSlug(response);
            setDetails({
              ...detailsRequest,
              keplerPointsAvailable: parseInt(fieldValues?.keplerPointsAvailable),
            });

            onNext();
            setIsLoading(false);
          }}
        >
          Next
        </Anchor>
      </footer>
    </div>
  );
}
